import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { subscriptionAPI, discountAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface FeeBreakdown {
  subtotal: number;
  discount_amount: number;
  discount_name: string;
  gross_total: number;
  vat_applicable: boolean;
  vat_percentage: number;
  vat_amount: number;
  total_after_vat: number;
  currency: string;
}

const CheckoutPage: React.FC = () => {
  useDocumentTitle('Checkout');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSubscriptionStatus, assets } = useAuth();

  const planId    = searchParams.get('plan');
  const period    = searchParams.get('period') || 'monthly';
  const paymentStatus = searchParams.get('payment');
  const tapId     = searchParams.get('tap_id');
  const tapStatus = searchParams.get('tap_status');

  const [loading, setLoading]       = useState(true);
  const [processing, setProcessing] = useState(false);
  const [plan, setPlan]             = useState<any>(null);
  const gateway: 'tap' | 'bank_transfer' = 'tap'; // PayPal removed

  // Discount
  const [discountCode, setDiscountCode]         = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountApplied, setDiscountApplied]   = useState(false);

  // Fee breakdown
  const [fees, setFees]             = useState<FeeBreakdown | null>(null);
  const [feesLoading, setFeesLoading] = useState(false);

  // Terms
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Payment result screens
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [countdown, setCountdown] = useState(8);
  const [verifying, setVerifying] = useState(false);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (!paymentSuccess) return;
    if (countdown <= 0) {
      navigate('/dashboard?subscription=activated');
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [paymentSuccess, countdown, navigate]);

  // -------------------------------------------------------
  // Handle Tap payment redirect-back — poll backend for real status
  // -------------------------------------------------------
  useEffect(() => {
    if (paymentStatus === 'complete' && tapId) {
      setLoading(false);
      setVerifying(true);

      let attempts = 0;
      const maxAttempts = 20; // 20 × 2s = 40s max

      const poll = async () => {
        try {
          const res = await subscriptionAPI.verifyPayment(tapId);
          const result = res.data.data;

          if (result.status === 'succeeded') {
            setSubscriptionStatus('active', null, null);
            setVerifying(false);
            setReceiptNumber(result.receipt_number || '');
            setPaymentSuccess(true);
            return;
          }

          if (result.status === 'failed') {
            setSubscriptionStatus(null, null, null);
            setVerifying(false);
            setPaymentFailed(true);
            setFailureMessage(result.failure_message || 'Your payment was declined. Please try again.');
            return;
          }

          // Still pending — retry
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            // Timeout — fallback: trust tap_status URL param
            setVerifying(false);
            if (tapStatus === 'CAPTURED' || tapStatus === 'APPROVED') {
              setSubscriptionStatus('active', null, null);
              setPaymentSuccess(true); // receipt_number may arrive slightly later via webhook
            } else {
              // Tap webhook may still be in-flight — don't call it "failed".
              // The webhook will activate the subscription in the background.
              setSubscriptionStatus(null, null, null);
              setPaymentFailed(true);
              setFailureMessage('Your payment is being verified. If you were charged, please check your Billing page in a moment — it may take up to a minute to reflect. Contact support if it does not update.');
            }
          }
        } catch {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            setVerifying(false);
            setSubscriptionStatus(null, null, null);
            setPaymentFailed(true);
            setFailureMessage('Could not verify payment. If you were charged, please check your Billing page in a moment.');
          }
        }
      };

      poll();
      return;
    }

    if (paymentStatus === 'canceled') {
      setSubscriptionStatus(null, null, null);
      toast.error('Payment was canceled.');
      navigate('/billing/plans');
      return;
    }

    if (planId) {
      fetchPlan();
    } else {
      setLoading(false);
    }
  }, [planId, paymentStatus, tapId, tapStatus]);

  const fetchPlan = async () => {
    try {
      const response = await subscriptionAPI.listPlans();
      const plansData = response.data.data;
      const plansArray = Array.isArray(plansData) ? plansData : (plansData?.plans || []);
      const selected = plansArray.find((p: any) => p.id === parseInt(planId!));
      setPlan(selected);
      if (selected) await fetchFees();
    } catch (error) {
      console.error('Failed to fetch plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async (discountCodeToUse?: string) => {
    if (!planId) return;
    setFeesLoading(true);
    try {
      const response = await subscriptionAPI.previewFees({
        plan_id: parseInt(planId),
        billing_period: period,
        discount_code: discountCodeToUse || undefined,
      });
      setFees(response.data.data);
    } catch (error) {
      console.error('Failed to fetch fees:', error);
    } finally {
      setFeesLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !plan) return;
    setDiscountValidating(true);
    try {
      const response = await discountAPI.validate(discountCode.trim(), plan.id, period);
      const result = response.data.data;
      if (result) {
        setDiscountApplied(true);
        toast.success(`Discount "${result.code_name}" applied!`);
        await fetchFees(discountCode.trim());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid discount code');
      setDiscountApplied(false);
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleRemoveDiscount = async () => {
    setDiscountApplied(false);
    setDiscountCode('');
    await fetchFees();
  };

  const handleSubscribe = async () => {
    if (!plan) return;
    setProcessing(true);
    try {
      const response = await subscriptionAPI.subscribe({
        plan_id: plan.id,
        billing_period: period,
        gateway,
        payment_token: 'pending',
        discount_code: discountApplied ? discountCode.trim() : undefined,
      });

      const result = response.data.data;

      if (result.redirect_url) {
        // Mark payment as pending BEFORE redirect so gate shows on return if user abandons
        setSubscriptionStatus('pending_payment', plan.id, period);
        window.location.href = result.redirect_url;
      } else {
        toast.success('Subscription created successfully!');
        setSubscriptionStatus('active', null, null);
        navigate('/billing');
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Payment failed';
      toast.error(msg);
      setProcessing(false);
    }
  };

  // -------------------------------------------------------
  // Verifying screen — shown while polling backend
  // -------------------------------------------------------
  if (verifying) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 8px 32px rgba(99,102,241,0.35)',
          }}>
            <div className="spinner-border text-white" style={{ width: '2rem', height: '2rem' }} role="status">
              <span className="visually-hidden">Processing…</span>
            </div>
          </div>
          <h2 className="fw-bold mb-2" style={{ color: '#1e293b' }}>Processing Your Payment</h2>
          <p className="text-muted">Please wait while we confirm your transaction. Do not close this page.</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------
  // Payment failed screen
  // -------------------------------------------------------
  if (paymentFailed) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: 'linear-gradient(135deg, #dc2626, #ef4444)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 8px 32px rgba(220,38,38,0.35)',
          }}>
            <i className="bi bi-x-lg" style={{ fontSize: 40, color: '#fff' }}></i>
          </div>
          <h2 className="fw-bold mb-2" style={{ color: '#7f1d1d' }}>Payment Failed</h2>
          <p className="text-muted mb-4">{failureMessage}</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button
              className="btn btn-danger btn-lg px-4"
              onClick={() => {
                const params = new URLSearchParams();
                if (planId) params.set('plan', planId);
                if (period) params.set('period', period);
                navigate(`/billing/checkout?${params.toString()}`);
              }}
            >
              <i className="bi bi-arrow-repeat me-2"></i>Try Again
            </button>
            <button
              className="btn btn-outline-secondary btn-lg"
              onClick={() => navigate('/billing/plans')}
            >
              <i className="bi bi-list me-2"></i>View Plans
            </button>
            <button
              className="btn btn-link text-muted"
              onClick={() => navigate('/billing')}
            >
              Go to Billing
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------
  // Payment success screen
  // -------------------------------------------------------
  if (paymentSuccess) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: 'linear-gradient(135deg, #059669, #10b981)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 8px 32px rgba(16,185,129,0.35)',
          }}>
            <i className="bi bi-check-lg" style={{ fontSize: 40, color: '#fff' }}></i>
          </div>
          <h2 className="fw-bold mb-2" style={{ color: '#065f46' }}>Payment Successful!</h2>
          <p className="text-muted mb-1" style={{ fontSize: '1.05rem' }}>
            Your subscription is now <strong className="text-success">active</strong>.
          </p>
          <p className="text-muted mb-4">
            Welcome aboard! Your full access has been unlocked.
          </p>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body py-3 px-4">
              <div className="d-flex align-items-center gap-3">
                <i className="bi bi-shield-check text-success" style={{ fontSize: 22 }}></i>
                <div className="text-start">
                  <div className="fw-semibold small">All features are now available</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>Analytics, monitoring, security, and more</div>
                </div>
              </div>
            </div>
          </div>

          {receiptNumber && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body py-3 px-4">
                <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-receipt text-primary" style={{ fontSize: 22 }}></i>
                  <div className="text-start">
                    <div className="fw-semibold small">Receipt #{receiptNumber}</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>A receipt has been emailed to your billing address</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!receiptNumber && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body py-3 px-4">
                <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-envelope-check text-primary" style={{ fontSize: 22 }}></i>
                  <div className="text-start">
                    <div className="fw-semibold small">Receipt email on its way</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>You will receive a payment confirmation to your billing email</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            className="btn btn-success btn-lg px-5 mb-3 w-100"
            onClick={() => navigate('/dashboard?subscription=activated')}
          >
            <i className="bi bi-speedometer2 me-2"></i>
            Go to Dashboard
          </button>
          <button
            className="btn btn-outline-secondary btn-lg px-4 mb-3"
            onClick={() => navigate('/billing')}
          >
            <i className="bi bi-receipt me-2"></i>
            View Billing &amp; Receipt
          </button>
          <div>
            <small className="text-muted">Redirecting automatically in {countdown}s...</small>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------
  // Loading / no-plan states
  // -------------------------------------------------------
  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center py-5"><LoadingSpinner /></div>
      </DashboardLayout>
    );
  }

  if (!plan) {
    return (
      <DashboardLayout>
        <div className="container-fluid text-center py-5">
          <h3>No plan selected</h3>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/billing/plans')}>
            View Plans
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const originalPrice = Number(period === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
  const displayTotal  = Number(fees ? fees.total_after_vat : originalPrice) || 0;

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <button
              className="btn btn-link text-decoration-none p-0 mb-3"
              onClick={() => navigate('/billing/plans')}
            >
              <i className="bi bi-arrow-left me-2"></i>Back to Plans
            </button>

            <div className="row g-4">
              {/* ---- Order Summary ---- */}
              <div className="col-md-5">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">Order Summary</h5>

                    <div className="border-bottom pb-3 mb-3">
                      <h4 className="fw-bold">{plan.name} Plan</h4>
                      <p className="text-muted text-capitalize mb-0">{period} billing</p>
                    </div>

                    {feesLoading ? (
                      <div className="text-center py-3">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Calculating…
                      </div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Subtotal</span>
                          <span>{fees?.currency || 'USD'} {fees ? Number(fees.subtotal).toFixed(2) : originalPrice.toFixed(2)}</span>
                        </div>

                        {fees && fees.discount_amount > 0 && (
                          <div className="d-flex justify-content-between mb-2 text-success">
                            <span>
                              Discount ({fees.discount_name})
                              <button
                                className="btn btn-link btn-sm text-danger p-0 ms-1"
                                onClick={handleRemoveDiscount}
                                style={{ fontSize: '0.75rem' }}
                              >
                                Remove
                              </button>
                            </span>
                            <span>-{fees.currency} {Number(fees.discount_amount).toFixed(2)}</span>
                          </div>
                        )}

                        {fees && fees.discount_amount > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span>Net Amount</span>
                            <span>{fees.currency} {Number(fees.gross_total).toFixed(2)}</span>
                          </div>
                        )}

                        {fees?.vat_applicable && (
                          <div className="d-flex justify-content-between mb-2">
                            <span>VAT ({fees.vat_percentage}%)</span>
                            <span>{fees.currency} {Number(fees.vat_amount).toFixed(2)}</span>
                          </div>
                        )}

                        <div className="border-top pt-3 mt-3">
                          <div className="d-flex justify-content-between">
                            <strong>Total</strong>
                            <strong className="h5 mb-0">
                              {fees?.currency || 'USD'} {displayTotal.toFixed(fees?.currency && ['KWD','BHD','OMR'].includes(fees.currency) ? 3 : 2)}
                            </strong>
                          </div>
                          <small className="text-muted">Billed {period}</small>
                          {fees?.vat_applicable && (
                            <div className="mt-1">
                              <small className="text-muted">Inclusive of {fees.vat_percentage}% VAT</small>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Discount code */}
                    <div className="border-top pt-3 mt-3">
                      <label className="form-label small fw-bold">Discount Code</label>
                      {discountApplied ? (
                        <div className="d-flex align-items-center bg-success bg-opacity-10 rounded p-2">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span className="small">{fees?.discount_name || discountCode} applied</span>
                        </div>
                      ) : (
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter code"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          />
                          <button
                            className="btn btn-outline-primary"
                            onClick={handleApplyDiscount}
                            disabled={discountValidating || !discountCode.trim()}
                          >
                            {discountValidating
                              ? <span className="spinner-border spinner-border-sm"></span>
                              : 'Apply'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Payment (Tap only) ---- */}
              <div className="col-md-7">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">
                      <i className="bi bi-credit-card me-2"></i>Payment
                    </h5>

                    {/* Payment info box */}
                    <div className="bg-light rounded p-3 mb-4">
                      <div className="d-flex align-items-start gap-2">
                        <i className="bi bi-shield-lock-fill text-primary mt-1"></i>
                        <div>
                          <p className="mb-1 small fw-semibold">Pay with our Secure Payment Gateway</p>
                          <p className="mb-0 text-muted small">
                            You will be redirected to a secure payment page to complete your purchase.
                            We do not store your card information.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Accepted cards */}
                    <div className="mb-4">
                      <small className="text-muted d-block mb-2">Accepted payment methods</small>
                      <div className="d-flex gap-2 flex-wrap">
                        {['Visa', 'Mastercard', 'mada', 'KNET', 'Apple Pay'].map(m => (
                          <span key={m} className="badge bg-light text-dark border" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Terms */}
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="agreeTerms"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="agreeTerms">
                        I agree to the{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>,{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                        and{' '}
                        <a href="/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>
                      </label>
                    </div>

                    <button
                      className="btn btn-primary btn-lg w-100"
                      onClick={handleSubscribe}
                      disabled={processing || !agreedToTerms}
                    >
                      {processing ? (
                        <><LoadingSpinner size="sm" /> Redirecting to Tap…</>
                      ) : (
                        <>
                          <i className="bi bi-lock me-2"></i>
                          Pay {fees?.currency || 'USD'} {displayTotal.toFixed(fees?.currency && ['KWD','BHD','OMR'].includes(fees.currency) ? 3 : 2)} Securely
                        </>
                      )}
                    </button>

                    <p className="text-muted text-center small mt-3 mb-0">
                      <i className="bi bi-lock me-1"></i>
                      Secure 256-bit SSL &mdash; we do not store your card information
                    </p>
                  </div>
                </div>

                {/* Bank transfer as secondary option */}
                <div className="card border-0 shadow-sm mt-3">
                  <div className="card-body p-3">
                    <p className="text-muted small mb-2">
                      <i className="bi bi-bank me-1"></i>
                      <strong>Prefer bank transfer?</strong> Go back to Plans and select Bank Transfer.
                    </p>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => navigate('/billing/plans')}
                    >
                      View other options
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CheckoutPage;
